import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { BlockStack } from '../block-stack/block-stack';
import { InlineStack } from '../inline-stack/inline-stack';
import { Typography } from '../typography/typography';
import { overlay, dialog } from './modal.css';

export interface ModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    children?: ReactNode;
    /** Footer actions (e.g. Cancel / Confirm buttons), laid out end-aligned. */
    actions?: ReactNode;
    testID?: string;
}

/**
 * Accessible centred dialog rendered in a portal. Closes on Escape and on
 * backdrop click; the panel is an `aria-modal` `dialog` labelled by its title.
 * Sizing is content-driven and the overlay scrolls, so it stays usable at large
 * text sizes.
 */
export function Modal({ open, onClose, title, children, actions, testID }: ModalProps) {
    const titleId = useId();

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    if (!open) return null;

    return createPortal(
        <div
            className={overlay}
            onClick={(e) => {
                // Only a click on the backdrop itself dismisses — not clicks that
                // bubble up from inside the panel.
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className={dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                data-testid={testID}
            >
                <BlockStack gap="400">
                    <Typography variant="heading-sm" as="h2" fontWeight="semibold">
                        <span id={titleId}>{title}</span>
                    </Typography>
                    {children}
                    {actions ? (
                        <InlineStack align="flex-end" gap="200" wrap>
                            {actions}
                        </InlineStack>
                    ) : null}
                </BlockStack>
            </div>
        </div>,
        document.body,
    );
}
